/**
 * QBridgeDownloadButton Component (E9)
 * Standalone download button with API integration
 *
 * Features:
 * - Fetches download info from API
 * - Shows version and file size
 * - Handles loading and error states
 * - Matches platform styling
 */

import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getQBridgeDownload } from '../../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  background: '#0F0F14',
  surface: 'rgba(255, 255, 255, 0.02)',
  border: 'rgba(255, 255, 255, 0.1)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  primary: '#27B56D',
  primaryLight: 'rgba(39, 181, 109, 0.1)',
  accent: '#7c3aed',
  accentLight: 'rgba(124, 58, 237, 0.1)',
  error: '#EF4444'
};

// Note: Download info is now fetched from getQBridgeDownload() API

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * QBridgeDownloadButton
 * @param {string} variant - 'primary' | 'secondary' | 'compact'
 * @param {function} onDownloadStart - Called when download starts
 * @param {function} onDownloadComplete - Called when download initiated
 * @param {boolean} showDetails - Show version and size
 */
const QBridgeDownloadButton = ({
  variant = 'primary',
  onDownloadStart,
  onDownloadComplete,
  showDetails = true
}) => {
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);

  // Fetch download info on mount
  useEffect(() => {
    const fetchDownloadInfo = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch from real API (with fallback built-in)
        const info = await getQBridgeDownload();
        setDownloadInfo(info);
      } catch (err) {
        console.error('Failed to fetch download info:', err);
        setError('Failed to load download info');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDownloadInfo();
  }, []);

  const handleDownload = async () => {
    if (isDownloading) return;

    // Check if download is available
    if (!downloadInfo?.url || downloadInfo?.available === false) {
      setError('Download coming soon. QBridge is in development.');
      return;
    }

    setIsDownloading(true);
    onDownloadStart?.();

    try {
      // Open download in new tab
      window.open(downloadInfo.url, '_blank');
      setDownloadStarted(true);
      onDownloadComplete?.();
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Check if download is available
  const isDownloadAvailable = downloadInfo?.url && downloadInfo?.available !== false;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER VARIANTS
  // ═══════════════════════════════════════════════════════════════════════════

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={20} sx={{ color: COLORS.textMuted }} />
        <Typography sx={{ color: COLORS.textMuted, fontSize: '0.875rem' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Compact variant (for inline use)
  if (variant === 'compact') {
    return (
      <Tooltip title={isDownloadAvailable
        ? `Download QBridge v${downloadInfo?.version} (${downloadInfo?.size})`
        : 'QBridge download coming soon'
      }>
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          size="small"
          startIcon={
            downloadStarted ? (
              <CheckCircleIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <DownloadIcon sx={{ fontSize: '1rem' }} />
            )
          }
          sx={{
            color: downloadStarted ? COLORS.primary : isDownloadAvailable ? COLORS.accent : COLORS.textMuted,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            padding: '4px 12px',
            borderRadius: '6px',
            backgroundColor: downloadStarted ? COLORS.primaryLight : isDownloadAvailable ? COLORS.accentLight : 'rgba(255,255,255,0.05)',
            '&:hover': {
              backgroundColor: downloadStarted
                ? 'rgba(39, 181, 109, 0.15)'
                : isDownloadAvailable
                ? 'rgba(124, 58, 237, 0.15)'
                : 'rgba(255,255,255,0.08)'
            }
          }}
        >
          {downloadStarted ? 'Downloaded' : isDownloadAvailable ? 'Download QBridge' : 'Coming Soon'}
        </Button>
      </Tooltip>
    );
  }

  // Secondary variant (outline style)
  if (variant === 'secondary') {
    return (
      <Box>
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          fullWidth
          startIcon={
            isDownloading ? (
              <CircularProgress size={18} color="inherit" />
            ) : downloadStarted ? (
              <CheckCircleIcon />
            ) : (
              <DownloadIcon />
            )
          }
          sx={{
            backgroundColor: 'transparent',
            border: `1px solid ${COLORS.border}`,
            color: isDownloadAvailable ? COLORS.text : COLORS.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            padding: '12px 24px',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: COLORS.surface,
              borderColor: isDownloadAvailable ? COLORS.accent : COLORS.border
            },
            '&.Mui-disabled': {
              borderColor: COLORS.border,
              color: COLORS.textMuted
            }
          }}
        >
          {isDownloading
            ? 'Downloading...'
            : downloadStarted
            ? 'Download Started'
            : isDownloadAvailable
            ? 'Download for Windows'
            : 'Coming Soon'}
        </Button>

        {showDetails && downloadInfo && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 1,
              color: COLORS.textMuted,
              fontSize: '0.75rem'
            }}
          >
            <Typography sx={{ fontSize: 'inherit' }}>
              v{downloadInfo.version}
            </Typography>
            <Typography sx={{ fontSize: 'inherit' }}>•</Typography>
            <Typography sx={{ fontSize: 'inherit' }}>
              {downloadInfo.size}
            </Typography>
            <Typography sx={{ fontSize: 'inherit' }}>•</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ComputerIcon sx={{ fontSize: '0.875rem' }} />
              <Typography sx={{ fontSize: 'inherit' }}>Windows</Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 1,
              color: COLORS.error
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />
            <Typography sx={{ fontSize: '0.75rem' }}>{error}</Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Primary variant (default - prominent style)
  return (
    <Box>
      <Button
        onClick={handleDownload}
        disabled={isDownloading}
        fullWidth
        startIcon={
          isDownloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : downloadStarted ? (
            <CheckCircleIcon />
          ) : (
            <DownloadIcon />
          )
        }
        sx={{
          background: downloadStarted
            ? `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`
            : isDownloadAvailable
            ? `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`
            : 'rgba(124, 58, 237, 0.4)',
          color: '#FFFFFF',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '14px 28px',
          borderRadius: '12px',
          boxShadow: downloadStarted
            ? `0 4px 20px ${COLORS.primaryLight}`
            : isDownloadAvailable
            ? '0 4px 20px rgba(124, 58, 237, 0.3)'
            : 'none',
          '&:hover': {
            background: downloadStarted
              ? `linear-gradient(135deg, #22C55E 0%, ${COLORS.primary} 100%)`
              : isDownloadAvailable
              ? `linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)`
              : 'rgba(124, 58, 237, 0.5)',
            boxShadow: downloadStarted
              ? `0 6px 24px rgba(39, 181, 109, 0.4)`
              : isDownloadAvailable
              ? '0 6px 24px rgba(124, 58, 237, 0.4)'
              : 'none'
          },
          '&.Mui-disabled': {
            background: 'rgba(124, 58, 237, 0.3)',
            color: 'rgba(255, 255, 255, 0.5)'
          }
        }}
      >
        {isDownloading
          ? 'Starting Download...'
          : downloadStarted
          ? 'Download Started!'
          : isDownloadAvailable
          ? 'Download QBridge + Q.AI Panel'
          : 'Coming Soon'}
      </Button>

      {showDetails && downloadInfo && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            mt: 1.5
          }}
        >
          {/* Version info line */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              color: COLORS.textMuted
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem' }}>
              v{downloadInfo.version}
            </Typography>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: COLORS.textMuted
              }}
            />
            <Typography sx={{ fontSize: '0.8125rem', color: COLORS.primary }}>
              Includes MT5 Panel
            </Typography>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: COLORS.textMuted
              }}
            />
            <Typography sx={{ fontSize: '0.8125rem' }}>
              {downloadInfo.size}
            </Typography>
          </Box>
          {/* System requirements line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.textMuted }}>
            <ComputerIcon sx={{ fontSize: '1rem' }} />
            <Typography sx={{ fontSize: '0.8125rem' }}>
              {downloadInfo.requirements?.os || 'Windows 10/11'}
            </Typography>
          </Box>
        </Box>
      )}

      {downloadStarted && (
        <Typography
          sx={{
            color: COLORS.primary,
            fontSize: '0.8125rem',
            textAlign: 'center',
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5
          }}
        >
          <CheckCircleIcon sx={{ fontSize: '1rem' }} />
          Check your Downloads folder
        </Typography>
      )}

      {error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            mt: 1,
            color: COLORS.error
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />
          <Typography sx={{ fontSize: '0.8125rem' }}>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STANDALONE CARD VARIANT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * QBridgeDownloadCard
 * Full card component for prominent display
 * @param {function} onDownloadStart - Called when download starts
 * @param {function} onDownloadComplete - Called when download initiated
 * @param {boolean} showUserGuide - Show User Guide link (default true)
 */
export const QBridgeDownloadCard = ({ onDownloadStart, onDownloadComplete, showUserGuide = true }) => {
  return (
    <Box
      sx={{
        backgroundColor: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '16px',
        padding: '24px',
        textAlign: 'center'
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '18px',
          background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 8px 32px rgba(124, 58, 237, 0.25)'
        }}
      >
        <DownloadIcon sx={{ color: '#FFFFFF', fontSize: '2.25rem' }} />
      </Box>

      {/* Title */}
      <Typography
        sx={{
          color: COLORS.text,
          fontSize: '1.25rem',
          fontWeight: 700,
          mb: 0.5,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Get Q.AI for MetaTrader 5
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          color: COLORS.primary,
          fontSize: '0.875rem',
          fontWeight: 500,
          mb: 1
        }}
      >
        One installer. Everything included.
      </Typography>

      {/* Features list */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          mb: 3,
          mx: 'auto',
          maxWidth: 'fit-content'
        }}
      >
        {[
          'QBridge background service',
          'Q.AI Panel EA for MT5',
          'Auto-starts on Windows boot',
          'Works with multiple MT5 terminals'
        ].map((feature, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: '0.875rem', color: COLORS.primary }} />
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8125rem' }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Download Button */}
      <QBridgeDownloadButton
        variant="primary"
        showDetails={true}
        onDownloadStart={onDownloadStart}
        onDownloadComplete={onDownloadComplete}
      />

      {/* User Guide Link */}
      {showUserGuide && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${COLORS.border}`
          }}
        >
          <Button
            component="a"
            href="/docs/user-guide"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<MenuBookIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              color: COLORS.textSecondary,
              textTransform: 'none',
              fontSize: '0.8125rem',
              '&:hover': {
                color: COLORS.accent,
                backgroundColor: 'transparent'
              }
            }}
          >
            User Guide
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QBridgeDownloadButton;
